import Header from './components/layout/Header';
import Hero from './components/sections/Hero';
import SocialProof from './components/sections/SocialProof';
// ComparisonStrip removed — redundant with the full Comparison table
import Comparison from './components/sections/Comparison';
import HowItWorks from './components/sections/HowItWorks';
import Infographic from './components/sections/Infographic';
import Features from './components/sections/Features';
import ParallaxBackground from './components/ui/ParallaxBackground';
import Integrations from './components/sections/Integrations';
import Pricing from './components/sections/Pricing';
import FAQ from './components/sections/FAQ';
import CTABanner from './components/sections/CTABanner';
import Footer from './components/layout/Footer';
import { ShimmerCard, ShimmerText } from './components/ui/ShimmerEffect';
import ErrorBoundary from './components/ErrorBoundary';
import SEO from './components/SEO';
import { ToastProvider } from './components/ui/Toast';
import { ThemeProvider } from './context/ThemeContext';
import AuthDrawer from './auth/AuthDrawer';
import { Box, Flex, Spinner } from '@chakra-ui/react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState, lazy, Suspense } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import './firebase';

// Lazy load authentication components
const Login = lazy(() => import('./auth/Login'));
const Signup = lazy(() => import('./auth/Signup'));
const LoggedIn = lazy(() => import('./auth/LoggedIn'));
const PaymentSuccess = lazy(() => import('./auth/PaymentSuccess'));
const PaymentError = lazy(() => import('./auth/PaymentError'));
const OAuthCallback = lazy(() => import('./auth/OAuthCallback'));

// Loading fallback component
const SuspenseLoader = () => (
  <Flex minH="100vh" align="center" justify="center" bg="landing.bg" position="relative">
    <Box position="absolute" top="0" left="0" right="0" bottom="0" bg="bg.hero" opacity="0.5" pointerEvents="none" />
    <Spinner size="xl" color="brand.600" borderWidth="3px" />
  </Flex>
);

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  const authMode = location.pathname === '/login'
    ? 'login'
    : location.pathname === '/signup'
      ? 'signup'
      : null;
  const isAuthPage = Boolean(authMode);
  const isCallbackPage = location.pathname === '/oauth/callback';

  const publicLandingPage = (
    <>
      <Box position="relative" bg="landing.bg">
        <ParallaxBackground />
        <Box position="relative" zIndex={1}><Hero /></Box>
      </Box>
      <SocialProof />
      <Infographic />
      <HowItWorks />
      <Features />
      <Integrations />
      <Pricing />
      <Comparison />
      <FAQ />
      <CTABanner />
    </>
  );

  const closeAuthDrawer = () => {
    if (isAuthPage) {
      navigate('/', { replace: true });
    }
  };

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        // User is signed in — force-refresh to validate the token is still good
        user.getIdToken(true)
          .then(() => {
            setIsLoggedIn(true);
            setAuthLoading(false);
          })
          .catch(() => {
            // Token is invalid; sign out and redirect
            setIsLoggedIn(false);
            setAuthLoading(false);
            if (location.pathname !== '/' && !isAuthPage && !isCallbackPage) {
              navigate('/', { replace: true });
            }
          });
      } else {
        // User is signed out
        setIsLoggedIn(false);
        setAuthLoading(false);
        if (location.pathname !== '/' && !isAuthPage && !isCallbackPage) {
          navigate('/', { replace: true });
        }
      }
    });

    return () => unsubscribe();
  }, [location.pathname, navigate, isAuthPage, isCallbackPage]);

  useEffect(() => {
    // If the user is logged in and they are on an auth page, redirect to home
    if (isLoggedIn && isAuthPage) {
      navigate('/', { replace: true });
    }
  }, [isLoggedIn, isAuthPage, navigate]);

  // Handle OAuth callback redirect from root URL
  // Some providers (like ClickUp) only allow domain-level redirect URLs
  // They redirect to http://localhost:3000/?code=...&state=...
  // We need to internally forward this to /oauth/callback for processing
  useEffect(() => {
    if (location.pathname === '/' && location.search.includes('code=')) {
      // Preserve all query parameters and redirect to callback route
      navigate('/oauth/callback' + location.search, { replace: true });
    }
  }, [location, navigate]);

  // Show loading spinner while checking authentication
  if (authLoading) {
    return (
      <Flex minH="100vh" align="center" justify="center" bg="landing.bg" position="relative">
        <Box position="absolute" top="0" left="0" right="0" bottom="0" bg="bg.hero" opacity="0.5" pointerEvents="none" />
        <Box textAlign="center" w="300px" position="relative">
          <ShimmerCard width="80px" height="80px" style={{
            borderRadius: '50%',
            margin: '0 auto 2rem',
            background: 'linear-gradient(90deg, #C5D8FB40 25%, #C5D8FB60 50%, #C5D8FB40 75%)',
            backgroundSize: '200% 100%'
          }} />
          <ShimmerText lines={1} width="60%" height="20px" style={{ margin: '0 auto' }} />
          <ShimmerText lines={1} width="40%" height="16px" style={{ margin: '1rem auto 0' }} />
        </Box>
      </Flex>
    );
  }

  return (
    <ErrorBoundary>
      <ThemeProvider>
      <ToastProvider>
      <SEO />
      <Box className={`pageBackground ${isLoggedIn ? 'theme-app' : 'theme-landing'}`} minH="100vh">
        {!isCallbackPage && !isLoggedIn && <Header />}
        <Box as="main">
          <Suspense fallback={<SuspenseLoader />}>
            <Routes>
              <Route path="/" element={
                isLoggedIn ? (
                  <LoggedIn />
                ) : (
                  publicLandingPage
                )
              } />
              <Route path="/login" element={isLoggedIn ? <LoggedIn /> : publicLandingPage} />
              <Route path="/signup" element={isLoggedIn ? <LoggedIn /> : publicLandingPage} />
              <Route path="/dashboard" element={<LoggedIn />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/error" element={<PaymentError />} />
              <Route path="/oauth/callback" element={<OAuthCallback />} />
              <Route path="*" element={
                <Flex minH="60vh" direction="column" align="center" justify="center" textAlign="center" p={8}>
                  <Box as="h1" fontSize="6rem" fontWeight="800" m={0} color="brand.600" lineHeight="1">404</Box>
                  <Box as="p" fontSize="1.25rem" color="var(--text-muted)" mt={2}>Page not found</Box>
                  <Box as="a" href="/" mt={6} color="brand.600" textDecoration="underline" fontSize="1rem">
                    Go home
                  </Box>
                </Flex>
              } />
            </Routes>
          </Suspense>

          {!isLoggedIn && !isCallbackPage && (
            <AuthDrawer mode={authMode} onClose={closeAuthDrawer}>
              <Suspense fallback={
                <Flex minH="320px" align="center" justify="center">
                  <Spinner size="lg" color="brand.600" borderWidth="3px" />
                </Flex>
              }>
                {authMode === 'login' ? <Login /> : null}
                {authMode === 'signup' ? <Signup /> : null}
              </Suspense>
            </AuthDrawer>
          )}
        </Box>
        {!isCallbackPage && !isLoggedIn && <Footer />}
      </Box>
      </ToastProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
